import { useEffect, useState } from "react";
import { Loader2, TrendingUp } from "lucide-react";
import api, { formatMoney } from "../lib/apiClient";

const RANGES = [
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
  { id: 36500, label: "All" },
];

export function TopProducts() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get("/admin/top-products", { params: { days, limit: 20 } })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days]);

  const products = data?.products || [];
  const maxUnits = products.length ? products[0].units : 0;

  return (
    <div className="card-soft rounded-2xl p-6 mb-6" data-testid="top-products-card">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="rounded-2xl p-3 bg-gradient-to-br from-[#f4e7c4] to-[#e6c665]/50 ring-1 ring-[#e6c665]/40">
            <TrendingUp className="h-6 w-6 text-[#8a681c]" aria-hidden="true" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg">Most Sold Products</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              {data ? `${data.total_units} units · ${formatMoney(data.total_revenue)} across paid orders` : "Best sellers by units sold in paid orders."}
            </p>
          </div>
        </div>
        <div className="flex gap-1 rounded-full bg-secondary p-1">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setDays(r.id)}
              data-testid={`top-products-range-${r.id}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${days === r.id ? "bg-white shadow text-foreground" : "text-muted-foreground"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline" aria-hidden="true" /></div>
      ) : products.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground" data-testid="top-products-empty">No paid orders yet in this range.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-products-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 px-3">Product</th>
                <th className="py-2 px-3 text-right">Units</th>
                <th className="py-2 px-3 text-right">Orders</th>
                <th className="py-2 pl-3 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={p.product_id} className="border-b border-border/50" data-testid={`top-products-row-${p.product_id}`}>
                  <td className="py-2 pr-3 text-muted-foreground font-semibold">{i + 1}</td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img src={p.image_url} alt="" className="w-9 h-9 rounded-lg object-cover bg-secondary shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-secondary shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{p.name}</div>
                        <div className="mt-1 h-1.5 w-28 rounded-full bg-secondary overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#e6c665] to-[#c59b27]" style={{ width: `${maxUnits ? Math.max(6, (p.units / maxUnits) * 100) : 0}%` }} />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right font-semibold" data-testid={`top-products-units-${p.product_id}`}>
                    {p.units}
                    {p.free_units > 0 && <span className="block text-[11px] font-normal text-muted-foreground">{p.free_units} free</span>}
                  </td>
                  <td className="py-2 px-3 text-right">{p.orders}</td>
                  <td className="py-2 pl-3 text-right">{formatMoney(p.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
